// src/pages/Users/ForgotPassword.jsx

import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Paper, Avatar, Typography, TextField, Button, Alert,
  Link, Fade, InputAdornment, CircularProgress,
} from '@mui/material';
import {
  MailOutline, EmailOutlined, MarkEmailReadOutlined, ArrowBack, ArrowForward, SendOutlined,
} from '@mui/icons-material';
import userApi from '../../api/userApi';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_SECONDS = 60;

// ───────────────────────── تصميم مشترك مع صفحة الدخول ─────────────────────────
const pageSx = {
  position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9999, overflow: 'auto', p: 2,
  background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #2e1065 100%)',
  '&::before': {
    content: '""', position: 'absolute', inset: 0, zIndex: 0,
    backgroundImage: `
      radial-gradient(circle at 20% 20%, rgba(99,102,241,.25) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(16,185,129,.15) 0%, transparent 50%)`,
  },
};

const cardSx = {
  position: 'relative', overflow: 'hidden', width: '100%',
  p: { xs: 4, sm: 5 }, borderRadius: '28px',
  display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
  background: 'rgba(255,255,255,.98)',
  boxShadow: '0 40px 80px -20px rgba(0,0,0,.6)',
};

const topBarSx = {
  position: 'absolute', top: 0, left: 0, height: 6, width: '100%',
  background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '14px', bgcolor: '#f8fafc' },
};

const primaryBtnSx = {
  py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: '14px',
  background: 'linear-gradient(90deg, #4F46E5 0%, #6366F1 100%)',
  boxShadow: '0 10px 25px -6px rgba(79,70,229,.4)',
  '&:hover': { background: 'linear-gradient(90deg, #3730A3 0%, #4F46E5 100%)' },
};

// إخفاء جزء من البريد: su****@gmail.com
const maskEmail = (email) => {
  const [name, domain] = email.split('@');
  if (!domain) return email;
  const visible = name.slice(0, 2);
  return `${visible}${'*'.repeat(Math.max(2, name.length - 2))}@${domain}`;
};

export default function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  const emailInvalid = touched && !EMAIL_REGEX.test(email.trim());

  // عدّاد إعادة الإرسال
  useEffect(() => {
    if (countdown <= 0) return;
    const id = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  const send = async () => {
    setError('');
    setLoading(true);
    try {
      await userApi.forgotPassword({ email: email.trim() });
      setSent(true);
      setCountdown(RESEND_SECONDS);
    } catch (err) {
      setError(err.response?.data?.message || t('auth.request_failed', 'حدث خطأ، حاول مرة أخرى'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!EMAIL_REGEX.test(email.trim())) return;
    send();
  };

  const BackIcon = isRTL ? ArrowForward : ArrowBack;

  return (
    <Box sx={pageSx} dir={isRTL ? 'rtl' : 'ltr'}>
      <Fade in timeout={800}>
        <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
          <Paper elevation={0} sx={cardSx}>
            <Box sx={topBarSx} />

            {!sent ? (
              <>
                <Avatar sx={{
                  mt: 1, mb: 2, width: 60, height: 60,
                  background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)',
                  boxShadow: '0 12px 24px -4px rgba(79,70,229,.4)',
                }}>
                  <MailOutline />
                </Avatar>

                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
                  {t('auth.forgot_password_title', 'نسيت كلمة المرور؟')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.8 }}>
                  {t('auth.forgot_password_hint', 'لا تقلق، أدخل بريدك الإلكتروني المسجّل وسنرسل لك رابطًا لتعيين كلمة مرور جديدة')}
                </Typography>

                {error && (
                  <Alert severity="error" variant="filled" sx={{ width: '100%', mb: 3, borderRadius: '12px', fontWeight: 600 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    autoFocus
                    type="email"
                    label={t('common.email', 'البريد الإلكتروني')}
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched(true)}
                    error={emailInvalid}
                    helperText={emailInvalid ? t('auth.invalid_email', 'أدخل بريدًا إلكترونيًا صحيحًا') : undefined}
                    inputProps={{ dir: 'ltr' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ ...inputSx, mb: 3 }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    endIcon={!loading && <SendOutlined sx={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} />}
                    sx={primaryBtnSx}
                  >
                    {loading
                      ? <CircularProgress size={24} color="inherit" />
                      : t('auth.send_reset_link', 'إرسال رابط الاستعادة')}
                  </Button>
                </Box>
              </>
            ) : (
              <Fade in>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{
                    mt: 1, mb: 2.5, width: 84, height: 84, borderRadius: '50%',
                    bgcolor: 'rgba(16,185,129,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <MarkEmailReadOutlined sx={{ fontSize: 44, color: '#10B981' }} />
                  </Box>

                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
                    {t('auth.check_your_email', 'تحقق من بريدك')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1.8 }}>
                    {t('auth.reset_link_sent_to', 'إذا كان البريد مسجلاً لدينا فقد أرسلنا رابط الاستعادة إلى')}
                  </Typography>
                  <Typography dir="ltr" sx={{ fontWeight: 800, color: '#4F46E5', my: 1 }}>
                    {maskEmail(email.trim())}
                  </Typography>

                  <Box sx={{
                    width: '100%', mt: 2, mb: 3, p: 2, borderRadius: '14px',
                    bgcolor: '#f8fafc', border: '1px solid #e2e8f0', textAlign: isRTL ? 'right' : 'left',
                  }}>
                    <Typography variant="caption" component="div" sx={{ color: 'text.secondary', lineHeight: 2 }}>
                      • {t('auth.tip_valid_30', 'الرابط صالح لمدة 30 دقيقة ولمرة واحدة')}<br />
                      • {t('auth.tip_check_spam', 'إذا لم تجده، تحقق من مجلد البريد غير المرغوب فيه')}
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={countdown > 0 || loading}
                    onClick={send}
                    sx={{ py: 1.3, borderRadius: '14px', fontWeight: 700, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                  >
                    {loading
                      ? <CircularProgress size={22} />
                      : countdown > 0
                        ? `${t('auth.resend_in', 'إعادة الإرسال بعد')} ${countdown} ${t('auth.seconds', 'ثانية')}`
                        : t('auth.resend', 'إعادة الإرسال')}
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => { setSent(false); setCountdown(0); }}
                    sx={{ mt: 1, fontWeight: 600, color: 'text.secondary' }}
                  >
                    {t('auth.use_another_email', 'استخدام بريد آخر')}
                  </Button>
                </Box>
              </Fade>
            )}

            <Link
              component={RouterLink}
              to="/login"
              sx={{
                mt: 3, display: 'inline-flex', alignItems: 'center', gap: 0.75,
                fontWeight: 700, fontSize: 14, textDecoration: 'none', color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <BackIcon sx={{ fontSize: 18 }} />
              {t('auth.back_to_login', 'العودة لتسجيل الدخول')}
            </Link>
          </Paper>
        </Container>
      </Fade>
    </Box>
  );
}